
import React, { useState, useEffect } from 'react'
import {X, Plus, Trash2, Move, Search, Calculator, ShoppingCart, GripVertical, ChevronDown, ChevronRight} from 'lucide-react'
import { useOrcamentos } from '../hooks/useOrcamentos'
import { useInsumos } from '../hooks/useInsumos'

interface OrcamentoFormProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
  initialData?: any
  title: string
  leadData?: any
}

interface ItemOrcamento {
  tipo: 'insumo' | 'composicao'
  itemId: string
  nome: string
  unidadeMedida: string
  quantidade: number
  precoUnitario: number
  precoUnitarioMaterial?: number
  precoUnitarioMaoObra?: number
  precoUnitarioEquipamento?: number
  precoUnitarioComposicao?: number
  tipoExecucao: 'execucao' | 'administracao'
  precoSemBDI: number
  precoComBDI: number
  subtotalSemBDI: number
  subtotalComBDI: number
}

interface Subetapa {
  codigo: string
  nome: string
  itens: ItemOrcamento[]
  subetapas?: Subetapa[]
}

interface Etapa {
  codigo: string
  nome: string
  itens: ItemOrcamento[]
  subetapas: Subetapa[]
}

// 🔥 DRAG & DROP TYPES
interface DragData {
  type: 'etapa' | 'subetapa' | 'item'
  etapaIndex: number
  subetapaPath?: number[]
  itemIndex?: number
}

interface DropZone {
  type: 'etapa' | 'subetapa' | 'item'
  etapaIndex: number
  subetapaPath?: number[]
  position?: 'before' | 'after' | 'inside'
  itemIndex?: number
  promoteToEtapa?: boolean // 🔥 NOVO: Para transformar subetapa em etapa
}

const OrcamentoForm: React.FC<OrcamentoFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  title,
  leadData
}) => {
  const { templates, getLeadsFechados } = useOrcamentos()
  const { insumos, composicoes } = useInsumos()
  
  const [formData, setFormData] = useState({
    leadId: '',
    nomeCliente: '',
    contatoCliente: '',
    nomeProjeto: '',
    descricao: '',
    status: 'rascunho' as const,
    dataEmissao: new Date().toISOString().split('T')[0],
    dataValidade: '',
    observacoes: '',
    templateId: '',
    // 🔥 NOVO: Campos BDI específicos por tipo de execução e material
    bdiAdministracaoMaoObra: 25,
    bdiAdministracaoMaterial: 20,
    bdiExecucaoMaoObra: 15,
    bdiExecucaoMaterial: 12
  })

  const [etapas, setEtapas] = useState<Etapa[]>([])
  const [leadsFechados, setLeadsFechados] = useState<any[]>([])
  const [showInsumoModal, setShowInsumoModal] = useState(false)
  const [currentEtapaIndex, setCurrentEtapaIndex] = useState(0)
  const [currentSubetapaPath, setCurrentSubetapaPath] = useState<number[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  // 🔥 NOVO: Filtro por tipo específico de insumo
  const [filterType, setFilterType] = useState<'all' | 'material' | 'mao_obra' | 'equipamento' | 'composicao'>('all')

  // 🔥 NOVO: Estado para controlar expansão de composições
  const [expandedComposicoes, setExpandedComposicoes] = useState<Set<string>>(new Set())

  // 🔥 DRAG & DROP STATES APRIMORADOS
  const [draggedItem, setDraggedItem] = useState<DragData | null>(null)
  const [dropTarget, setDropTarget] = useState<DropZone | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragPreview, setDragPreview] = useState<string>('')

  // 🔥 NOVA FUNÇÃO: Verificar se é modo de edição
  const isEditMode = !!initialData

  // 🔥 NOVA FUNÇÃO: Verificar se informações básicas estão preenchidas
  const hasBasicInfo = () => {
    return formData.nomeCliente || formData.nomeProjeto || formData.contatoCliente || formData.descricao
  }

  // 🔥 NOVA FUNÇÃO: Estado para interface colapsível "Informações Básicas"
  const [isBasicInfoCollapsed, setIsBasicInfoCollapsed] = useState(() => {
    // Recuperar estado da sessão
    const savedState = sessionStorage.getItem('orcamento-basic-info-collapsed')
    return savedState ? JSON.parse(savedState) : false
  })

  // 🔥 NOVA FUNÇÃO: Persistir estado colapsível
  const toggleBasicInfoCollapse = () => {
    const newState = !isBasicInfoCollapsed
    setIsBasicInfoCollapsed(newState)
    sessionStorage.setItem('orcamento-basic-info-collapsed', JSON.stringify(newState))
  }

  // 🔥 NOVA FUNÇÃO: Toggle expansão de composição
  const toggleComposicaoExpansion = (composicaoId: string) => {
    const newExpanded = new Set(expandedComposicoes)
    if (newExpanded.has(composicaoId)) {
      newExpanded.delete(composicaoId)
    } else {
      newExpanded.add(composicaoId)
    }
    setExpandedComposicoes(newExpanded)
  }

  // 🔥 NOVA FUNÇÃO: Load leads em negociação apenas
  useEffect(() => {
    const loadLeadsEmNegociacao = async () => {
      const leads = await getLeadsFechados()
      // 🔥 FILTRAR: apenas leads com status "negociacao"
      const leadsEmNegociacao = leads.filter(lead => lead.status === 'negociacao')
      setLeadsFechados(leadsEmNegociacao)
    }
    if (isOpen) {
      loadLeadsEmNegociacao()
    }
  }, [isOpen, getLeadsFechados])

  // Initialize form with lead data if provided
  useEffect(() => {
    if (leadData) {
      setFormData(prev => ({
        ...prev,
        leadId: leadData._id,
        nomeCliente: leadData.nome,
        contatoCliente: leadData.contato,
        nomeProjeto: `Projeto - ${leadData.nome}`,
        descricao: leadData.observacoes || ''
      }))
    }
  }, [leadData])

  // Initialize form with existing data
  useEffect(() => {
    if (initialData) {
      setFormData({
        leadId: initialData.leadId || '',
        nomeCliente: initialData.nomeCliente || '',
        contatoCliente: initialData.contatoCliente || '',
        nomeProjeto: initialData.nomeProjeto || '',
        descricao: initialData.descricao || '',
        status: initialData.status || 'rascunho',
        dataEmissao: initialData.dataEmissao?.split('T')[0] || new Date().toISOString().split('T')[0],
        dataValidade: initialData.dataValidade?.split('T')[0] || '',
        observacoes: initialData.observacoes || '',
        templateId: initialData.templateId || '',
        // 🔥 NOVO: Inicializar BDI específicos
        bdiAdministracaoMaoObra: initialData.bdiAdministracaoMaoObra || 25,
        bdiAdministracaoMaterial: initialData.bdiAdministracaoMaterial || 20,
        bdiExecucaoMaoObra: initialData.bdiExecucaoMaoObra || 15,
        bdiExecucaoMaterial: initialData.bdiExecucaoMaterial || 12
      })
      setEtapas(initialData.etapas || [])
    }
  }, [initialData])

  // 🔥 FUNÇÃO: Gerar código recursivo infinito
  const generateRecursiveCode = (parentCode: string, index: number): string => {
    return parentCode ? `${parentCode}.${index + 1}` : `${index + 1}`
  }

  // 🔥 FUNÇÃO: Reorganizar etapas com numeração recursiva infinita
  const reorganizeEtapas = (newEtapas: Etapa[]): Etapa[] => {
    return newEtapas.map((etapa, index) => {
      const newCode = generateRecursiveCode('', index)
      return {
        ...etapa,
        codigo: newCode,
        subetapas: reorganizeSubetapasRecursive(etapa.subetapas || [], newCode)
      }
    })
  }

  // 🔥 FUNÇÃO: Reorganizar subetapas recursivamente com numeração infinita
  const reorganizeSubetapasRecursive = (subetapas: Subetapa[], parentCode: string): Subetapa[] => {
    if (!Array.isArray(subetapas)) return []
    
    return subetapas.map((subetapa, index) => {
      if (!subetapa) return { codigo: '', nome: '', itens: [], subetapas: [] }
      
      const newCode = generateRecursiveCode(parentCode, index)
      return {
        ...subetapa,
        codigo: newCode,
        subetapas: subetapa.subetapas ? reorganizeSubetapasRecursive(subetapa.subetapas, newCode) : []
      }
    })
  }

  // 🔥 FUNÇÃO: Handle etapa code change with reorganization
  const handleEtapaCodeChange = (etapaIndex: number, newCode: string) => {
    const newEtapas = [...etapas]
    newEtapas[etapaIndex].codigo = newCode
    
    const reorganizedEtapas = reorganizeEtapas(newEtapas)
    setEtapas(reorganizedEtapas)
  }

  // 🔥 FUNÇÃO: Obter referência de subetapa por path
  const getSubetapaByPath = (etapas: Etapa[], etapaIndex: number, subetapaPath: number[]): Subetapa | null => {
    if (!etapas[etapaIndex] || !subetapaPath.length) return null
    
    let currentSubetapas = etapas[etapaIndex].subetapas || []
    let target: Subetapa | null = null
    
    for (let i = 0; i < subetapaPath.length; i++) {
      const index = subetapaPath[i]
      if (!currentSubetapas[index]) return null
      
      target = currentSubetapas[index]
      if (i < subetapaPath.length - 1) {
        currentSubetapas = target.subetapas || []
      }
    }
    
    return target
  }

  // 🔥 FUNÇÃO: Remover item/subetapa por path
  const removeByPath = (etapas: Etapa[], dragData: DragData): any => {
    const newEtapas = [...etapas]
    
    if (dragData.type === 'etapa') {
      return newEtapas.splice(dragData.etapaIndex, 1)[0]
    }
    
    if (dragData.type === 'item') {
      if (!dragData.subetapaPath || dragData.subetapaPath.length === 0) {
        // Item direto da etapa
        return newEtapas[dragData.etapaIndex].itens.splice(dragData.itemIndex!, 1)[0]
      } else {
        // Item de subetapa
        const subetapa = getSubetapaByPath(newEtapas, dragData.etapaIndex, dragData.subetapaPath)
        if (subetapa && dragData.itemIndex !== undefined) {
          return subetapa.itens.splice(dragData.itemIndex, 1)[0]
        }
      }
    }
    
    if (dragData.type === 'subetapa' && dragData.subetapaPath) {
      if (dragData.subetapaPath.length === 1) {
        // Subetapa direta da etapa
        return newEtapas[dragData.etapaIndex].subetapas.splice(dragData.subetapaPath[0], 1)[0]
      } else {
        // Subetapa aninhada
        const parentPath = dragData.subetapaPath.slice(0, -1)
        const parentSubetapa = getSubetapaByPath(newEtapas, dragData.etapaIndex, parentPath)
        if (parentSubetapa && parentSubetapa.subetapas) {
          const lastIndex = dragData.subetapaPath[dragData.subetapaPath.length - 1]
          return parentSubetapa.subetapas.splice(lastIndex, 1)[0]
        }
      }
    }
    
    return null
  }

  // 🔥 FUNÇÃO: Inserir item/subetapa por path
  const insertByPath = (etapas: Etapa[], dropZone: DropZone, item: any): void => {
    // 🔥 NOVO: Verificar se deve promover subetapa para etapa
    if (dropZone.promoteToEtapa && item.codigo) {
      // Transformar subetapa em etapa
      const newEtapa: Etapa = {
        codigo: '', // Será reorganizado depois
        nome: item.nome,
        itens: item.itens || [],
        subetapas: item.subetapas || []
      }
      
      const insertIndex = dropZone.position === 'before' ? dropZone.etapaIndex : dropZone.etapaIndex + 1
      etapas.splice(insertIndex, 0, newEtapa)
      return
    }
    
    if (dropZone.type === 'etapa') {
      if (dropZone.position === 'inside') {
        // Inserir dentro da etapa
        if (item.tipo) {
          // É um item
          etapas[dropZone.etapaIndex].itens.push(item)
        } else {
          // É uma subetapa
          if (!etapas[dropZone.etapaIndex].subetapas) {
            etapas[dropZone.etapaIndex].subetapas = []
          }
          etapas[dropZone.etapaIndex].subetapas.push(item)
        }
      } else {
        // Inserir antes/depois da etapa
        const insertIndex = dropZone.position === 'before' ? dropZone.etapaIndex : dropZone.etapaIndex + 1
        etapas.splice(insertIndex, 0, item)
      }
    } else if (dropZone.type === 'subetapa') {
      if (dropZone.position === 'inside') {
        // Inserir dentro da subetapa
        const subetapa = getSubetapaByPath(etapas, dropZone.etapaIndex, dropZone.subetapaPath!)
        if (subetapa) {
          if (item.tipo) {
            // É um item
            subetapa.itens.push(item)
          } else {
            // É uma subetapa
            if (!subetapa.subetapas) {
              subetapa.subetapas = []
            }
            subetapa.subetapas.push(item)
          }
        }
      } else {
        // Inserir antes/depois da subetapa
        const parentPath = dropZone.subetapaPath!.slice(0, -1)
        const targetIndex = dropZone.subetapaPath![dropZone.subetapaPath!.length - 1]
        
        if (parentPath.length === 0) {
          // Subetapa direta da etapa
          const insertIndex = dropZone.position === 'before' ? targetIndex : targetIndex + 1
          etapas[dropZone.etapaIndex].subetapas.splice(insertIndex, 0, item)
        } else {
          // Subetapa aninhada
          const parentSubetapa = getSubetapaByPath(etapas, dropZone.etapaIndex, parentPath)
          if (parentSubetapa && parentSubetapa.subetapas) {
            const insertIndex = dropZone.position === 'before' ? targetIndex : targetIndex + 1
            parentSubetapa.subetapas.splice(insertIndex, 0, item)
          }
        }
      }
    } else if (dropZone.type === 'item') {
      // Inserir antes/depois do item
      if (!dropZone.subetapaPath || dropZone.subetapaPath.length === 0) {
        // Item direto da etapa
        const insertIndex = dropZone.position === 'before' ? dropZone.itemIndex! : dropZone.itemIndex! + 1
        etapas[dropZone.etapaIndex].itens.splice(insertIndex, 0, item)
      } else {
        // Item de subetapa
        const subetapa = getSubetapaByPath(etapas, dropZone.etapaIndex, dropZone.subetapaPath)
        if (subetapa) {
          const insertIndex = dropZone.position === 'before' ? dropZone.itemIndex! : dropZone.itemIndex! + 1
          subetapa.itens.splice(insertIndex, 0, item)
        }
      }
    }
  }

  // 🔥 FUNÇÃO: Gerar preview do código de destino APRIMORADO
  const generateDropPreview = (dropZone: DropZone): string => {
    // 🔥 NOVO: Preview para promoção de subetapa para etapa
    if (dropZone.promoteToEtapa) {
      const newIndex = dropZone.position === 'before' ? dropZone.etapaIndex : dropZone.etapaIndex + 1
      return `🔄 Promover para Etapa ${newIndex + 1}`
    }
    
    if (dropZone.type === 'etapa') {
      if (dropZone.position === 'inside') {
        const etapa = etapas[dropZone.etapaIndex]
        return `📁 Dentro de: ${etapa.codigo} - ${etapa.nome}`
      } else {
        const newIndex = dropZone.position === 'before' ? dropZone.etapaIndex : dropZone.etapaIndex + 1
        return `📍 Posição: Etapa ${newIndex + 1}`
      }
    } else if (dropZone.type === 'subetapa') {
      const subetapa = getSubetapaByPath(etapas, dropZone.etapaIndex, dropZone.subetapaPath!)
      if (dropZone.position === 'inside') {
        return `📂 Dentro de: ${subetapa?.codigo} - ${subetapa?.nome}`
      } else {
        const parentPath = dropZone.subetapaPath!.slice(0, -1)
        const parentCode = parentPath.length === 0 
          ? etapas[dropZone.etapaIndex].codigo 
          : getSubetapaByPath(etapas, dropZone.etapaIndex, parentPath)?.codigo || ''
        const targetIndex = dropZone.subetapaPath![dropZone.subetapaPath!.length - 1]
        const newIndex = dropZone.position === 'before' ? targetIndex : targetIndex + 1
        return `📍 Posição: ${parentCode}.${newIndex + 1}`
      }
    } else if (dropZone.type === 'item') {
      const location = !dropZone.subetapaPath || dropZone.subetapaPath.length === 0
        ? `etapa ${etapas[dropZone.etapaIndex].codigo}`
        : `subetapa ${getSubetapaByPath(etapas, dropZone.etapaIndex, dropZone.subetapaPath)?.codigo}`
      const newIndex = dropZone.position === 'before' ? dropZone.itemIndex! : dropZone.itemIndex! + 1
      return `🎯 ${dropZone.position === 'before' ? 'Antes' : 'Depois'} do item ${newIndex + 1} em ${location}`
    }
    return ''
  }

  // 🔥 DRAG & DROP HANDLERS APRIMORADOS

  const handleDragStart = (e: React.DragEvent, dragData: DragData) => {
    setDraggedItem(dragData)
    setIsDragging(true)
    e.dataTransfer.effectAllowed = 'move'
    
    // Definir dados para o drag
    e.dataTransfer.setData('text/plain', JSON.stringify(dragData))
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
    setDropTarget(null)
    setIsDragging(false)
    setDragPreview('')
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e: React.DragEvent, dropZone: DropZone) => {
    e.preventDefault()
    if (draggedItem) {
      // 🔥 NOVO: Verificar se é subetapa sendo arrastada para área de etapa (promoção)
      if (draggedItem.type === 'subetapa' && dropZone.type === 'etapa' && 
          (dropZone.position === 'before' || dropZone.position === 'after')) {
        dropZone.promoteToEtapa = true
      }
      
      setDropTarget(dropZone)
      setDragPreview(generateDropPreview(dropZone))
    }
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Só limpar se realmente saiu do elemento
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDropTarget(null)
      setDragPreview('')
    }
  }

  const handleDrop = (e: React.DragEvent, dropZone: DropZone) => {
    e.preventDefault()
    
    if (!draggedItem) return
    
    // Verificar se é um movimento válido
    if (draggedItem.type === 'etapa' && dropZone.type === 'item') return
    
    const newEtapas = [...etapas]
    
    // Remover item da posição original
    const removedItem = removeByPath(newEtapas, draggedItem)
    
    if (removedItem) {
      // Inserir na nova posição
      insertByPath(newEtapas, dropZone, removedItem)
      
      // Reorganizar códigos
      const reorganizedEtapas = reorganizeEtapas(newEtapas)
      setEtapas(reorganizedEtapas)
    }
    
    handleDragEnd()
  }

  // 🔥 FUNÇÃO: Calcular BDI específico baseado no tipo de execução e tipo de insumo
  const calculateSpecificBDI = (item: any, tipoExecucao: 'execucao' | 'administracao') => {
    const precoBase = Number(item.precoUnitario || item.valorTotal || 0)
    let bdiAplicado = 0

    if (item.tipo === 'insumo') {
      const insumo = insumos.find(i => i._id === item.itemId)
      const tipoInsumo = insumo?.tipo || 'material'

      // Determinar BDI baseado no tipo de execução e tipo de insumo
      if (tipoExecucao === 'administracao') {
        if (tipoInsumo === 'mao_obra') {
          bdiAplicado = formData.bdiAdministracaoMaoObra
        } else {
          bdiAplicado = formData.bdiAdministracaoMaterial // material, equipamento
        }
      } else { // execucao
        if (tipoInsumo === 'mao_obra') {
          bdiAplicado = formData.bdiExecucaoMaoObra
        } else {
          bdiAplicado = formData.bdiExecucaoMaterial // material, equipamento
        }
      }
    } else if (item.tipo === 'composicao') {
      // Para composições, usar BDI de material como padrão
      if (tipoExecucao === 'administracao') {
        bdiAplicado = formData.bdiAdministracaoMaterial
      } else {
        bdiAplicado = formData.bdiExecucaoMaterial
      }
    }

    const precoSemBDI = precoBase
    const precoComBDI = precoSemBDI * (1 + bdiAplicado / 100)
    
    return {
      precoSemBDI,
      precoComBDI,
      bdiAplicado
    }
  }

  // 🔥 FUNÇÃO: Recalcular todos os itens quando BDI mudar
  const recalculateAllItemsWithSpecificBDI = (newEtapas: Etapa[]) => {
    const processItems = (items: ItemOrcamento[]) => {
      return items.map(item => {
        if (!item) return item
        
        const { precoSemBDI, precoComBDI } = calculateSpecificBDI(item, item.tipoExecucao)
        
        return {
          ...item,
          precoSemBDI,
          precoComBDI,
          subtotalSemBDI: precoSemBDI * item.quantidade,
          subtotalComBDI: precoComBDI * item.quantidade
        }
      })
    }

    const processSubetapasRecursive = (subetapas: Subetapa[]): Subetapa[] => {
      if (!Array.isArray(subetapas)) return []
      
      return subetapas.map(subetapa => {
        if (!subetapa) return subetapa
        
        return {
          ...subetapa,
          itens: processItems(subetapa.itens || []),
          subetapas: subetapa.subetapas ? processSubetapasRecursive(subetapa.subetapas) : []
        }
      })
    }

    return newEtapas.map(etapa => {
      if (!etapa) return etapa
      
      return {
        ...etapa,
        itens: processItems(etapa.itens || []),
        subetapas: processSubetapasRecursive(etapa.subetapas || [])
      }
    })
  }

  // 🔥 FUNÇÃO: Handle BDI change específico
  const handleSpecificBDIChange = (field: string, value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    // Recalcular todos os itens com os novos BDIs específicos
    const updatedEtapas = recalculateAllItemsWithSpecificBDI(etapas)
    setEtapas(updatedEtapas)
  }

  // 🔥 FUNÇÃO: Calcular totais da etapa (formato texto)
  const calculateEtapaBreakdown = (etapa: Etapa) => {
    if (!etapa) return {
      material: 0, materialComBDI: 0, equipamento: 0, equipamentoComBDI: 0,
      maoObra: 0, maoObraComBDI: 0, composicao: 0, composicaoComBDI: 0,
      total: 0, totalComBDI: 0
    }

    let materialTotal = 0
    let materialTotalComBDI = 0
    let equipamentoTotal = 0
    let equipamentoTotalComBDI = 0
    let maoObraTotal = 0
    let maoObraTotalComBDI = 0
    let composicaoTotal = 0
    let composicaoTotalComBDI = 0

    // Calcular itens da etapa
    if (Array.isArray(etapa.itens)) {
      etapa.itens.forEach(item => {
        if (!item || !item.tipo) return
        
        if (item.tipo === 'insumo') {
          const insumo = insumos.find(i => i._id === item.itemId)
          if (insumo && insumo.tipo) {
            switch (insumo.tipo) {
              case 'material':
                materialTotal += item.subtotalSemBDI || 0
                materialTotalComBDI += item.subtotalComBDI || 0
                break
              case 'equipamento':
                equipamentoTotal += item.subtotalSemBDI || 0
                equipamentoTotalComBDI += item.subtotalComBDI || 0
                break
              case 'mao_obra':
                maoObraTotal += item.subtotalSemBDI || 0
                maoObraTotalComBDI += item.subtotalComBDI || 0
                break
              default:
                materialTotal += item.subtotalSemBDI || 0
                materialTotalComBDI += item.subtotalComBDI || 0
                break
            }
          } else {
            // Se não encontrar o insumo ou não tiver tipo, categorizar como material
            materialTotal += item.subtotalSemBDI || 0
            materialTotalComBDI += item.subtotalComBDI || 0
          }
        } else if (item.tipo === 'composicao') {
          composicaoTotal += item.subtotalSemBDI || 0
          composicaoTotalComBDI += item.subtotalComBDI || 0
        }
      })
    }

    // Calcular itens das subetapas recursivamente
    const calculateSubetapasRecursive = (subetapas: Subetapa[]) => {
      if (!Array.isArray(subetapas)) return
      
      subetapas.forEach(subetapa => {
        if (!subetapa) return
        
        if (Array.isArray(subetapa.itens)) {
          subetapa.itens.forEach(item => {
            if (!item || !item.tipo) return
            
            if (item.tipo === 'insumo') {
              const insumo = insumos.find(i => i._id === item.itemId)
              if (insumo && insumo.tipo) {
                switch (insumo.tipo) {
                  case 'material':
                    materialTotal += item.subtotalSemBDI || 0
                    materialTotalComBDI += item.subtotalComBDI || 0
                    break
                  case 'equipamento':
                    equipamentoTotal += item.subtotalSemBDI || 0
                    equipamentoTotalComBDI += item.subtotalComBDI || 0
                    break
                  case 'mao_obra':
                    maoObraTotal += item.subtotalSemBDI || 0
                    maoObraTotalComBDI += item.subtotalComBDI || 0
                    break
                  default:
                    materialTotal += item.subtotalSemBDI || 0
                    materialTotalComBDI += item.subtotalComBDI || 0
                    break
                }
              } else {
                // Se não encontrar o insumo ou não tiver tipo, categorizar como material
                materialTotal += item.subtotalSemBDI || 0
                materialTotalComBDI += item.subtotalComBDI || 0
              }
            } else if (item.tipo === 'composicao') {
              composicaoTotal += item.subtotalSemBDI || 0
              composicaoTotalComBDI += item.subtotalComBDI || 0
            }
          })
        }

        if (subetapa.subetapas) {
          calculateSubetapasRecursive(subetapa.subetapas)
        }
      })
    }

    calculateSubetapasRecursive(etapa.subetapas || [])

    const total = materialTotal + equipamentoTotal + maoObraTotal + composicaoTotal
    const totalComBDI = materialTotalComBDI + equipamentoTotalComBDI + maoObraTotalComBDI + composicaoTotalComBDI

    return {
      material: materialTotal,
      materialComBDI: materialTotalComBDI,
      equipamento: equipamentoTotal,
      equipamentoComBDI: equipamentoTotalComBDI,
      maoObra: maoObraTotal,
      maoObraComBDI: maoObraTotalComBDI,
      composicao: composicaoTotal,
      composicaoComBDI: composicaoTotalComBDI,
      total,
      totalComBDI
    }
  }

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = etapas.reduce((total, etapa) => {
      const breakdown = calculateEtapaBreakdown(etapa)
      return total + breakdown.total
    }, 0)

    const totalComBDI = etapas.reduce((total, etapa) => {
      const breakdown = calculateEtapaBreakdown(etapa)
      return total + breakdown.totalComBDI
    }, 0)

    return { subtotal, total: totalComBDI }
  }

  // Handle lead selection
  const handleLeadSelect = (leadId: string) => {
    const lead = leadsFechados.find(l => l._id === leadId)
    if (lead) {
      setFormData(prev => ({
        ...prev,
        leadId: lead._id,
        nomeCliente: lead.nome,
        contatoCliente: lead.contato,
        nomeProjeto: `Projeto - ${lead.nome}`,
        descricao: lead.observacoes || ''
      }))
    }
  }

  // Handle template selection
  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find(t => t._id === templateId)
    if (template) {
      setFormData(prev => ({
        ...prev,
        templateId: template._id
      }))
      setEtapas(template.etapas)
    }
  }

  // Add new etapa
  const addEtapa = () => {
    const newEtapa: Etapa = {
      codigo: (etapas.length + 1).toString(),
      nome: `Etapa ${etapas.length + 1}`,
      itens: [],
      subetapas: []
    }
    const newEtapas = [...etapas, newEtapa]
    const reorganizedEtapas = reorganizeEtapas(newEtapas)
    setEtapas(reorganizedEtapas)
  }

  // 🔥 FUNÇÃO: Adicionar subetapa recursivamente
  const addSubetapa = (etapaIndex: number, subetapaPath: number[] = []) => {
    const newEtapas = [...etapas]
    
    if (subetapaPath.length === 0) {
      // Adicionar subetapa diretamente à etapa
      const newSubetapa: Subetapa = {
        codigo: `${newEtapas[etapaIndex].codigo}.${(newEtapas[etapaIndex].subetapas || []).length + 1}`,
        nome: `Subetapa ${(newEtapas[etapaIndex].subetapas || []).length + 1}`,
        itens: [],
        subetapas: []
      }
      if (!newEtapas[etapaIndex].subetapas) {
        newEtapas[etapaIndex].subetapas = []
      }
      newEtapas[etapaIndex].subetapas.push(newSubetapa)
    } else {
      // Navegar recursivamente até a subetapa correta
      const targetSubetapa = getSubetapaByPath(newEtapas, etapaIndex, subetapaPath)
      
      if (targetSubetapa) {
        if (!targetSubetapa.subetapas) {
          targetSubetapa.subetapas = []
        }
        
        const newSubetapa: Subetapa = {
          codigo: `${targetSubetapa.codigo}.${targetSubetapa.subetapas.length + 1}`,
          nome: `Subetapa ${targetSubetapa.subetapas.length + 1}`,
          itens: [],
          subetapas: []
        }
        
        targetSubetapa.subetapas.push(newSubetapa)
      }
    }
    
    // Reorganizar códigos
    const reorganizedEtapas = reorganizeEtapas(newEtapas)
    setEtapas(reorganizedEtapas)
  }

  // Toggle item selection
  const toggleItemSelection = (itemId: string) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId)
    } else {
      newSelected.add(itemId)
    }
    setSelectedItems(newSelected)
  }

  // Select all filtered items
  const selectAllItems = () => {
    const filteredItemIds = getAllFilteredItems().map(item => item._id)
    setSelectedItems(new Set(filteredItemIds))
  }

  // Clear selection
  const clearSelection = () => {
    setSelectedItems(new Set())
  }

  // 🔥 FUNÇÃO MODIFICADA: Get all filtered items com filtro por tipo específico
  const getAllFilteredItems = () => {
    const allItems = []
    
    // 🔥 NOVO: Filtrar insumos por tipo específico
    if (filterType === 'all' || ['material', 'mao_obra', 'equipamento'].includes(filterType)) {
      const filteredInsumos = insumos.filter(insumo => {
        const matchesSearch = insumo.nome.toLowerCase().includes(searchTerm.toLowerCase())
        const isActive = insumo.ativo
        const matchesType = filterType === 'all' || insumo.tipo === filterType
        
        return matchesSearch && isActive && matchesType
      })
      
      allItems.push(...filteredInsumos.map(item => ({ ...item, tipo: 'insumo' })))
    }
    
    // 🔥 NOVO: Filtrar composições separadamente
    if (filterType === 'all' || filterType === 'composicao') {
      const filteredComposicoes = composicoes.filter(comp => {
        const matchesSearch = comp.nome.toLowerCase().includes(searchTerm.toLowerCase())
        const isActive = comp.ativo
        
        return matchesSearch && isActive
      })
      
      allItems.push(...filteredComposicoes.map(item => ({ ...item, tipo: 'composicao' })))
    }
    
    return allItems
  }

  // 🔥 FUNÇÃO MODIFICADA: Adicionar itens selecionados com consulta de composição
  const addSelectedItemsToTarget = () => {
    const allItems = getAllFilteredItems()
    const itemsToAdd = allItems.filter(item => selectedItems.has(item._id))
    
    if (itemsToAdd.length === 0) {
      return
    }

    const newEtapas = [...etapas]
    
    if (!newEtapas[currentEtapaIndex]) {
      console.error('Etapa não encontrada')
      return
    }
    
    itemsToAdd.forEach(item => {
      const tipoExecucao: 'execucao' | 'administracao' = 'execucao' // Default
      const { precoSemBDI, precoComBDI } = calculateSpecificBDI(item, tipoExecucao)
      
      const newItem: ItemOrcamento = {
        tipo: item.tipo as 'insumo' | 'composicao',
        itemId: item._id,
        nome: item.nome,
        unidadeMedida: item.unidadeMedida,
        quantidade: 1,
        precoUnitario: Number(item.precoUnitario || item.valorTotal || 0),
        tipoExecucao,
        precoSemBDI,
        precoComBDI,
        subtotalSemBDI: precoSemBDI,
        subtotalComBDI: precoComBDI
      }

      // 🔥 NOVA LÓGICA: Consultar itens da composição e distribuir preços
      if (item.tipo === 'composicao') {
        const composicaoData = composicoes.find(c => c._id === item._id)
        
        if (composicaoData && composicaoData.itens && Array.isArray(composicaoData.itens)) {
          let precoMaterial = 0
          let precoMaoObra = 0
          let precoEquipamento = 0
          
          // Somar preços por categoria
          composicaoData.itens.forEach(subItem => {
            if (subItem.tipo === 'insumo') {
              const insumoInfo = insumos.find(ins => ins._id === subItem.itemId)
              if (insumoInfo) {
                const precoSubItem = Number(subItem.precoUnitario || 0) * Number(subItem.quantidade || 1)
                
                switch (insumoInfo.tipo) {
                  case 'material':
                    precoMaterial += precoSubItem
                    break
                  case 'mao_obra':
                    precoMaoObra += precoSubItem
                    break
                  case 'equipamento':
                    precoEquipamento += precoSubItem
                    break
                  default:
                    precoMaterial += precoSubItem // Default para material
                    break
                }
              }
            }
            // Se for composição aninhada, somar no valor total da composição
          })
          
          // Definir preços específicos baseados na consulta dos itens
          newItem.precoUnitarioMaterial = precoMaterial
          newItem.precoUnitarioMaoObra = precoMaoObra
          newItem.precoUnitarioEquipamento = precoEquipamento
          newItem.precoUnitarioComposicao = newItem.precoUnitario
        } else {
          // Fallback se não conseguir consultar os itens
          newItem.precoUnitarioComposicao = newItem.precoUnitario
        }
      } else if (item.tipo === 'insumo') {
        // Lógica original para insumos
        const insumo = insumos.find(i => i._id === item._id)
        if (insumo && insumo.tipo) {
          switch (insumo.tipo) {
            case 'material':
              newItem.precoUnitarioMaterial = newItem.precoUnitario
              break
            case 'mao_obra':
              newItem.precoUnitarioMaoObra = newItem.precoUnitario
              break
            case 'equipamento':
              newItem.precoUnitarioEquipamento = newItem.precoUnitario
              break
          }
        } else {
          // Se não encontrar o insumo ou não tiver tipo, usar como material
          newItem.precoUnitarioMaterial = newItem.precoUnitario
        }
      }
      
      // Adicionar item no local correto
      if (currentSubetapaPath.length === 0) {
        newEtapas[currentEtapaIndex].itens.push(newItem)
      } else {
        const targetSubetapa = getSubetapaByPath(newEtapas, currentEtapaIndex, currentSubetapaPath)
        if (targetSubetapa) {
          targetSubetapa.itens.push(newItem)
        }
      }
    })
    
    setEtapas(newEtapas)
    setSelectedItems(new Set())
    setShowInsumoModal(false)
  }

  // 🔥 FUNÇÃO: Atualizar item com cálculos BDI automáticos específicos
  const updateItemField = (etapaIndex: number, subetapaPath: number[], itemIndex: number, field: string, value: any) => {
    const newEtapas = [...etapas]
    let targetItem: ItemOrcamento | undefined

    if (subetapaPath.length === 0) {
      targetItem = newEtapas[etapaIndex].itens[itemIndex]
    } else {
      const subetapa = getSubetapaByPath(newEtapas, etapaIndex, subetapaPath)
      if (subetapa) {
        targetItem = subetapa.itens[itemIndex]
      }
    }

    if (targetItem) {
      (targetItem as any)[field] = value

      // Recalcular preços quando necessário
      if (['quantidade', 'tipoExecucao'].includes(field)) {
        const { precoSemBDI, precoComBDI } = calculateSpecificBDI(targetItem, targetItem.tipoExecucao)
        
        targetItem.precoSemBDI = precoSemBDI
        targetItem.precoComBDI = precoComBDI
        targetItem.subtotalSemBDI = precoSemBDI * targetItem.quantidade
        targetItem.subtotalComBDI = precoComBDI * targetItem.quantidade
      }
    }

    setEtapas(newEtapas)
  }

  // 🔥 FUNÇÃO: Remover item
  const removeItem = (etapaIndex: number, subetapaPath: number[], itemIndex: number) => {
    const newEtapas = [...etapas]
    
    if (subetapaPath.length === 0) {
      newEtapas[etapaIndex].itens.splice(itemIndex, 1)
    } else {
      const subetapa = getSubetapaByPath(newEtapas, etapaIndex, subetapaPath)
      if (subetapa) {
        subetapa.itens.splice(itemIndex, 1)
      }
    }
    
    setEtapas(newEtapas)
  }

  // 🔥 FUNÇÃO: Renderizar subetapas recursivamente com drag & drop
  const renderSubetapas = (subetapas: Subetapa[], etapaIndex: number, parentPath: number[] = [], level: number = 1) => {
    if (!Array.isArray(subetapas)) return null
    
    return subetapas.map((subetapa, subetapaIndex) => {
      if (!subetapa) return null
      
      const currentPath = [...parentPath, subetapaIndex]
      const dragData: DragData = { type: 'subetapa', etapaIndex, subetapaPath: currentPath }
      const isDropTarget = dropTarget?.type === 'subetapa' && 
                          dropTarget?.etapaIndex === etapaIndex &&
                          JSON.stringify(dropTarget?.subetapaPath) === JSON.stringify(currentPath)
      
      return (
        <div key={subetapaIndex} className="mb-4">
          {/* Drop zone antes da subetapa */}
          <div
            className={`h-2 transition-all duration-200 ${
              isDropTarget && dropTarget?.position === 'before' 
                ? 'bg-blue-400 border-2 border-dashed border-blue-500' 
                : 'bg-transparent'
            }`}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, { 
              type: 'subetapa', 
              etapaIndex, 
              subetapaPath: currentPath, 
              position: 'before' 
            })}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, { 
              type: 'subetapa', 
              etapaIndex, 
              subetapaPath: currentPath, 
              position: 'before' 
            })}
          />
          
          <div 
            className={`bg-gray-50 rounded-lg transition-all duration-200 ${
              isDropTarget && dropTarget?.position === 'inside' 
                ? 'ring-2 ring-blue-400 bg-blue-50' 
                : ''
            }`}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, { 
              type: 'subetapa', 
              etapaIndex, 
              subetapaPath: currentPath, 
              position: 'inside' 
            })}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, { 
              type: 'subetapa', 
              etapaIndex, 
              subetapaPath: currentPath, 
              position: 'inside' 
            })}
          >
            <div className="p-4">
              <div className="flex items-center gap-4 mb-4">
                <div
                  draggable
                  onDragStart={(e) => handleDragStart(e, dragData)}
                  onDragEnd={handleDragEnd}
                  className="cursor-move p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  <GripVertical size={16} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={subetapa.codigo || ''}
                  onChange={(e) => {
                    const newEtapas = [...etapas]
                    const targetSubetapa = getSubetapaByPath(newEtapas, etapaIndex, currentPath)
                    if (targetSubetapa) {
                      targetSubetapa.codigo = e.target.value
                    }
                    setEtapas(newEtapas)
                  }}
                  placeholder="Código"
                  className="w-24 p-2 border border-gray-300 rounded-md"
                />
                <input
                  type="text"
                  value={subetapa.nome || ''}
                  onChange={(e) => {
                    const newEtapas = [...etapas]
                    const targetSubetapa = getSubetapaByPath(newEtapas, etapaIndex, currentPath)
                    if (targetSubetapa) {
                      targetSubetapa.nome = e.target.value
                    }
                    setEtapas(newEtapas)
                  }}
                  placeholder="Nome da subetapa"
                  className="flex-1 p-2 border border-gray-300 rounded-md"
                />
                <button
                  type="button"
                  onClick={() => addSubetapa(etapaIndex, currentPath)}
                  className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm"
                  title="Adicionar Subetapa"
                >
                  <Plus size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentEtapaIndex(etapaIndex)
                    setCurrentSubetapaPath(currentPath)
                    setShowInsumoModal(true)
                  }}
                  className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm flex items-center gap-2"
                >
                  <ShoppingCart size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const newEtapas = [...etapas]
                    removeByPath(newEtapas, dragData)
                    const reorganizedEtapas = reorganizeEtapas(newEtapas)
                    setEtapas(reorganizedEtapas)
                  }}
                  className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Items da subetapa */}
              {subetapa.itens && subetapa.itens.length > 0 && (
                <div className="mb-4">
                  {renderItemsTable(subetapa.itens, etapaIndex, currentPath)}
                </div>
              )}

              {/* Subetapas recursivas */}
              {subetapa.subetapas && subetapa.subetapas.length > 0 && (
                <div>
                  {renderSubetapas(subetapa.subetapas, etapaIndex, currentPath, level + 1)}
                </div>
              )}
            </div>
          </div>
          
          {/* Drop zone depois da subetapa */}
          <div
            className={`h-2 transition-all duration-200 ${
              isDropTarget && dropTarget?.position === 'after' 
                ? 'bg-blue-400 border-2 border-dashed border-blue-500' 
                : 'bg-transparent'
            }`}
            onDragOver={handleDragOver}
            onDragEnter={(e) => handleDragEnter(e, { 
              type: 'subetapa', 
              etapaIndex, 
              subetapaPath: currentPath, 
              position: 'after' 
            })}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, { 
              type: 'subetapa', 
              etapaIndex, 
              subetapaPath: currentPath, 
              position: 'after' 
            })}
          />
        </div>
      )
    })
  }

  // 🔥 FUNÇÃO: Renderizar tabela de itens com nova ordem de colunas e layout
  const renderItemsTable = (itens: ItemOrcamento[], etapaIndex: number, subetapaPath: number[] = []) => {
    if (!Array.isArray(itens)) return null
    
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left p-3 border w-8"></th>
              <th className="text-left p-3 border w-80">Nome do Item</th>
              <th className="text-left p-3 border w-32">Categoria</th>
              <th className="text-left p-3 border w-40">Tipo Execução</th>
              <th className="text-left p-3 border w-24">Quantidade</th>
              <th className="text-left p-3 border w-24">Unidade</th>
              <th className="text-left p-3 border w-32">Preço Unit. Material</th>
              <th className="text-left p-3 border w-32">Preço Unit. Mão de Obra</th>
              <th className="text-left p-3 border w-40">Subtotal</th>
              <th className="text-left p-3 border w-20"></th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item, itemIndex) => {
              if (!item) return null
              
              const insumoInfo = item.tipo === 'insumo' 
                ? insumos.find(i => i._id === item.itemId)
                : null

              const composicaoInfo = item.tipo === 'composicao'
                ? composicoes.find(c => c._id === item.itemId)
                : null

              const isExpanded = expandedComposicoes.has(item.itemId)
              const dragData: DragData = { type: 'item', etapaIndex, subetapaPath, itemIndex }
              const isDropTarget = dropTarget?.type === 'item' && 
                                  dropTarget?.etapaIndex === etapaIndex &&
                                  JSON.stringify(dropTarget?.subetapaPath) === JSON.stringify(subetapaPath) &&
                                  dropTarget?.itemIndex === itemIndex

              return (
                <React.Fragment key={itemIndex}>
                  {/* Drop zone antes do item */}
                  <tr>
                    <td colSpan={10} className="p-0">
                      <div
                        className={`h-1 transition-all duration-200 ${
                          isDropTarget && dropTarget?.position === 'before' 
                            ? 'bg-blue-400 border-2 border-dashed border-blue-500' 
                            : 'bg-transparent'
                        }`}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, { 
                          type: 'item', 
                          etapaIndex, 
                          subetapaPath, 
                          itemIndex, 
                          position: 'before' 
                        })}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, { 
                          type: 'item', 
                          etapaIndex, 
                          subetapaPath, 
                          itemIndex, 
                          position: 'before' 
                        })}
                      />
                    </td>
                  </tr>
                  
                  <tr 
                    className={`border-b transition-all duration-200 ${
                      isDropTarget && dropTarget?.position === 'after' 
                        ? 'bg-blue-50' 
                        : 'hover:bg-gray-50'
                    }`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, dragData)}
                    onDragEnd={handleDragEnd}
                  >
                    <td className="p-3 border w-8">
                      <div className="cursor-move p-1 hover:bg-gray-200 rounded transition-colors">
                        <GripVertical size={16} className="text-gray-400" />
                      </div>
                    </td>
                    
                    {/* Nome do Item */}
                    <td className="p-3 border w-80">
                      <div className="flex items-center gap-2">
                        {item.tipo === 'composicao' && composicaoInfo && (
                          <button
                            type="button"
                            onClick={() => toggleComposicaoExpansion(item.itemId)}
                            className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100"
                            title={isExpanded ? 'Recolher itens' : 'Expandir itens'}
                          >
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        )}
                        <div className="font-medium">{item.nome || ''}</div>
                      </div>
                    </td>
                    
                    {/* 🔥 CORRIGIDO: Categoria com tipo específico de insumo */}
                    <td className="p-3 border w-32">
                      {item.tipo === 'insumo' && insumoInfo && insumoInfo.tipo && (
                        <span className={`px-2 py-1 rounded text-xs ${
                          insumoInfo.tipo === 'material' ? 'bg-green-100 text-green-800' :
                          insumoInfo.tipo === 'equipamento' ? 'bg-orange-100 text-orange-800' :
                          insumoInfo.tipo === 'mao_obra' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {insumoInfo.tipo === 'mao_obra' ? 'Mão de Obra' : 
                           insumoInfo.tipo.charAt(0).toUpperCase() + insumoInfo.tipo.slice(1)}
                        </span>
                      )}
                      {item.tipo === 'composicao' && (
                        <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs">
                          Composição
                        </span>
                      )}
                      {item.tipo === 'insumo' && (!insumoInfo || !insumoInfo.tipo) && (
                        <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs">
                          Material
                        </span>
                      )}
                    </td>
                    
                    {/* Tipo Execução */}
                    <td className="p-3 border w-40">
                      <select
                        value={item.tipoExecucao || 'execucao'}
                        onChange={(e) => updateItemField(etapaIndex, subetapaPath, itemIndex, 'tipoExecucao', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="execucao">Execução</option>
                        <option value="administracao">Administração</option>
                      </select>
                    </td>
                    
                    {/* Quantidade */}
                    <td className="p-3 border w-24">
                      <input
                        type="number"
                        step="0.01"
                        value={item.quantidade || 0}
                        onChange={(e) => updateItemField(etapaIndex, subetapaPath, itemIndex, 'quantidade', Number(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded"
                      />
                    </td>
                    
                    {/* Unidade */}
                    <td className="p-3 border w-24">{item.unidadeMedida || ''}</td>
                    
                    {/* 🔥 CORRIGIDO: Preço Unit. Material */}
                    <td className="p-3 border w-32">
                      <div className="text-sm">
                        {item.tipo === 'insumo' && insumoInfo && insumoInfo.tipo === 'material' && (
                          <div>R$ {(item.precoUnitarioMaterial || 0).toFixed(2)}</div>
                        )}
                        {item.tipo === 'insumo' && (!insumoInfo || !insumoInfo.tipo) && (
                          <div>R$ {(item.precoUnitarioMaterial || item.precoUnitario || 0).toFixed(2)}</div>
                        )}
                        {item.tipo === 'composicao' && (
                          <div>R$ {(item.precoUnitarioMaterial || 0).toFixed(2)}</div>
                        )}
                        {item.tipo === 'insumo' && insumoInfo && insumoInfo.tipo === 'equipamento' && (
                          <div>R$ {(item.precoUnitarioEquipamento || 0).toFixed(2)}</div>
                        )}
                        {item.tipo === 'insumo' && insumoInfo && insumoInfo.tipo === 'mao_obra' && (
                          <div className="text-gray-400">-</div>
                        )}
                      </div>
                    </td>
                    
                    {/* 🔥 CORRIGIDO: Preço Unit. Mão de Obra */}
                    <td className="p-3 border w-32">
                      <div className="text-sm">
                        {item.tipo === 'insumo' && insumoInfo && insumoInfo.tipo === 'mao_obra' && (
                          <div>R$ {(item.precoUnitarioMaoObra || item.precoUnitario || 0).toFixed(2)}</div>
                        )}
                        {item.tipo === 'composicao' && (
                          <div>R$ {(item.precoUnitarioMaoObra || 0).toFixed(2)}</div>
                        )}
                        {item.tipo === 'insumo' && insumoInfo && insumoInfo.tipo !== 'mao_obra' && (
                          <div className="text-gray-400">-</div>
                        )}
                        {item.tipo === 'insumo' && (!insumoInfo || !insumoInfo.tipo) && (
                          <div className="text-gray-400">-</div>
                        )}
                      </div>
                    </td>
                    
                    {/* 🔥 NOVO: Subtotal com valor com BDI em azul acima */}
                    <td className="p-3 border w-40">
                      <div className="text-sm">
                        <div className="text-blue-600 font-semibold text-xs mb-1">
                          R$ {(item.subtotalComBDI || 0).toFixed(2)}
                        </div>
                        <div className="font-medium text-gray-900">
                          R$ {(item.subtotalSemBDI || 0).toFixed(2)}
                        </div>
                      </div>
                    </td>
                    
                    {/* 🔥 AJUSTE: Ações sem texto */}
                    <td className="p-3 border w-20">
                      <button
                        type="button"
                        onClick={() => removeItem(etapaIndex, subetapaPath, itemIndex)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>

                  {/* 🔥 CORRIGIDO: Mostrar itens da composição quando expandida com valores nas colunas corretas */}
                  {item.tipo === 'composicao' && isExpanded && composicaoInfo && composicaoInfo.itens && (
                    composicaoInfo.itens.map((subItem, subIndex) => {
                      const subInsumoInfo = subItem.tipo === 'insumo' 
                        ? insumos.find(i => i._id === subItem.itemId)
                        : null
                      
                      const subComposicaoInfo = subItem.tipo === 'composicao'
                        ? composicoes.find(c => c._id === subItem.itemId)
                        : null

                      return (
                        <tr key={`${itemIndex}-sub-${subIndex}`} className="bg-blue-50 border-b">
                          <td className="p-3 border w-8">
                            <div className="ml-6 text-gray-400">
                              <div className="w-4 h-4 border-l-2 border-b-2 border-gray-300"></div>
                            </div>
                          </td>
                          <td className="p-3 border w-80">
                            <div className="ml-6 text-sm text-gray-700">
                              {subInsumoInfo?.nome || subComposicaoInfo?.nome || 'Item não encontrado'}
                            </div>
                          </td>
                          <td className="p-3 border w-32">
                            {subItem.tipo === 'insumo' && subInsumoInfo && subInsumoInfo.tipo && (
                              <span className={`px-2 py-1 rounded text-xs ${
                                subInsumoInfo.tipo === 'material' ? 'bg-green-100 text-green-800' :
                                subInsumoInfo.tipo === 'equipamento' ? 'bg-orange-100 text-orange-800' :
                                subInsumoInfo.tipo === 'mao_obra' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {subInsumoInfo.tipo === 'mao_obra' ? 'Mão de Obra' : 
                                 subInsumoInfo.tipo.charAt(0).toUpperCase() + subInsumoInfo.tipo.slice(1)}
                              </span>
                            )}
                            {subItem.tipo === 'composicao' && (
                              <span className="px-2 py-1 rounded bg-blue-100 text-blue-800 text-xs">
                                Composição
                              </span>
                            )}
                          </td>
                          <td className="p-3 border w-40 text-gray-500 text-sm">-</td>
                          <td className="p-3 border w-24 text-sm text-gray-700">
                            {(subItem.quantidade || 0).toFixed(2)}
                          </td>
                          <td className="p-3 border w-24 text-sm text-gray-700">
                            {subInsumoInfo?.unidadeMedida || subComposicaoInfo?.unidadeMedida || ''}
                          </td>
                          {/* 🔥 CORRIGIDO: Exibir valores na coluna correta baseado no tipo */}
                          <td className="p-3 border w-32 text-sm text-gray-700">
                            {subItem.tipo === 'insumo' && subInsumoInfo && (subInsumoInfo.tipo === 'material' || subInsumoInfo.tipo === 'equipamento') && (
                              <div>R$ {(subItem.precoUnitario || 0).toFixed(2)}</div>
                            )}
                            {subItem.tipo === 'composicao' && (
                              <div>R$ {(subItem.precoUnitario || 0).toFixed(2)}</div>
                            )}
                            {subItem.tipo === 'insumo' && subInsumoInfo && subInsumoInfo.tipo === 'mao_obra' && (
                              <div className="text-gray-400">-</div>
                            )}
                            {subItem.tipo === 'insumo' && (!subInsumoInfo || !subInsumoInfo.tipo) && (
                              <div>R$ {(subItem.precoUnitario || 0).toFixed(2)}</div>
                            )}
                          </td>
                          <td className="p-3 border w-32 text-sm text-gray-700">
                            {subItem.tipo === 'insumo' && subInsumoInfo && subInsumoInfo.tipo === 'mao_obra' && (
                              <div>R$ {(subItem.precoUnitario || 0).toFixed(2)}</div>
                            )}
                            {subItem.tipo === 'insumo' && subInsumoInfo && subInsumoInfo.tipo !== 'mao_obra' && (
                              <div className="text-gray-400">-</div>
                            )}
                            {subItem.tipo === 'composicao' && (
                              <div className="text-gray-400">-</div>
                            )}
                            {subItem.tipo === 'insumo' && (!subInsumoInfo || !subInsumoInfo.tipo) && (
                              <div className="text-gray-400">-</div>
                            )}
                          </td>
                          <td className="p-3 border w-40 text-sm text-gray-700">
                            R$ {(subItem.subtotal || 0).toFixed(2)}
                          </td>
                          <td className="p-3 border w-20"></td>
                        </tr>
                      )
                    })
                  )}
                  
                  {/* Drop zone depois do item */}
                  <tr>
                    <td colSpan={10} className="p-0">
                      <div
                        className={`h-1 transition-all duration-200 ${
                          isDropTarget && dropTarget?.position === 'after' 
                            ? 'bg-blue-400 border-2 border-dashed border-blue-500' 
                            : 'bg-transparent'
                        }`}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, { 
                          type: 'item', 
                          etapaIndex, 
                          subetapaPath, 
                          itemIndex, 
                          position: 'after' 
                        })}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, { 
                          type: 'item', 
                          etapaIndex, 
                          subetapaPath, 
                          itemIndex, 
                          position: 'after' 
                        })}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  // Handle submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    const { subtotal, total } = calculateTotals()
    
    const submitData = {
      ...formData,
      dataEmissao: new Date(formData.dataEmissao).toISOString(),
      dataValidade: new Date(formData.dataValidade).toISOString(),
      etapas,
      valorSubtotal: subtotal,
      valorTotal: total
    }

    await onSubmit(submitData)
    onClose()
  }

  const { subtotal, total } = calculateTotals()
  const filteredItems = getAllFilteredItems()

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white w-full h-full flex flex-col">
        {/* Header fixo */}
        <div className="sticky top-0 bg-white border-b p-6 z-10 shadow-sm">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100">
              <X size={24} />
            </button>
          </div>
          
          {/* 🔥 NOVO: Indicador de drag & drop APRIMORADO */}
          {isDragging && dragPreview && (
            <div className="mt-2 p-3 bg-blue-100 border border-blue-300 rounded-md text-blue-800 text-sm">
              <strong>Movendo para:</strong> {dragPreview}
            </div>
          )}
        </div>

        {/* Conteúdo scrollável */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-8">
            {/* 🔥 CORRIGIDO: Interface colapsível funciona tanto para novos quanto para edição */}
            {hasBasicInfo() ? (
              <div className="bg-gray-50 rounded-lg">
                <div 
                  className="p-6 cursor-pointer flex items-center justify-between"
                  onClick={toggleBasicInfoCollapse}
                >
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2">Informações Básicas</h3>
                    <div className="text-sm text-gray-600">
                      <div><strong>Projeto:</strong> {formData.nomeProjeto}</div>
                      {formData.descricao && (
                        <div><strong>Descrição:</strong> {formData.descricao}</div>
                      )}
                    </div>
                  </div>
                  <div className="ml-4">
                    {isBasicInfoCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
                  </div>
                </div>
                
                {!isBasicInfoCollapsed && (
                  <div className="px-6 pb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* Lead Selection */}
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Lead (Opcional)
                        </label>
                        <select
                          value={formData.leadId}
                          onChange={(e) => handleLeadSelect(e.target.value)}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Selecionar lead em negociação...</option>
                          {leadsFechados.map(lead => (
                            <option key={lead._id} value={lead._id}>
                              {lead.nome} - {lead.contato}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* 🔥 CONDICIONAL: Template Selection apenas se não for edição */}
                      {!isEditMode && (
                        <div className="lg:col-span-1">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Template (Opcional)
                          </label>
                          <select
                            value={formData.templateId}
                            onChange={(e) => handleTemplateSelect(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">Criar do zero...</option>
                            {templates.filter(t => t.ativo).map(template => (
                              <option key={template._id} value={template._id}>
                                {template.nome} - {template.categoria}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Status */}
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="rascunho">Rascunho</option>
                          <option value="enviado">Enviado</option>
                          <option value="aprovado">Aprovado</option>
                          <option value="perdido">Perdido</option>
                          <option value="expirado">Expirado</option>
                        </select>
                      </div>

                      {/* Client Info */}
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nome do Cliente *
                        </label>
                        <input
                          type="text"
                          value={formData.nomeCliente}
                          onChange={(e) => setFormData(prev => ({ ...prev, nomeCliente: e.target.value }))}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contato do Cliente
                        </label>
                        <input
                          type="text"
                          value={formData.contatoCliente}
                          onChange={(e) => setFormData(prev => ({ ...prev, contatoCliente: e.target.value }))}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Project Info */}
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nome do Projeto *
                        </label>
                        <input
                          type="text"
                          value={formData.nomeProjeto}
                          onChange={(e) => setFormData(prev => ({ ...prev, nomeProjeto: e.target.value }))}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Dates */}
                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Emissão *
                        </label>
                        <input
                          type="date"
                          value={formData.dataEmissao}
                          onChange={(e) => setFormData(prev => ({ ...prev, dataEmissao: e.target.value }))}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="lg:col-span-1">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Data de Validade *
                        </label>
                        <input
                          type="date"
                          value={formData.dataValidade}
                          onChange={(e) => setFormData(prev => ({ ...prev, dataValidade: e.target.value }))}
                          required
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* 🔥 NOVO: Campos BDI Específicos por Tipo de Execução e Material */}
                      <div className="lg:col-span-3">
                        <h4 className="text-md font-semibold text-gray-700 mb-4 border-b pb-2">
                          Configuração de BDI por Tipo de Execução e Material
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              BDI Administração Mão de Obra (%)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.bdiAdministracaoMaoObra}
                              onChange={(e) => handleSpecificBDIChange('bdiAdministracaoMaoObra', Number(e.target.value))}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              BDI Administração Material (%)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.bdiAdministracaoMaterial}
                              onChange={(e) => handleSpecificBDIChange('bdiAdministracaoMaterial', Number(e.target.value))}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              BDI Execução Mão de Obra (%)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.bdiExecucaoMaoObra}
                              onChange={(e) => handleSpecificBDIChange('bdiExecucaoMaoObra', Number(e.target.value))}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              BDI Execução Material (%)
                            </label>
                            <input
                              type="number"
                              step="0.01"
                              value={formData.bdiExecucaoMaterial}
                              onChange={(e) => handleSpecificBDIChange('bdiExecucaoMaterial', Number(e.target.value))}
                              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descrição do Projeto
                      </label>
                      <textarea
                        value={formData.descricao}
                        onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // 🔥 CORRIGIDO: Seção expandida quando vazia (tanto para novos quanto edição)
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-6">Informações Básicas</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Lead Selection */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lead (Opcional)
                    </label>
                    <select
                      value={formData.leadId}
                      onChange={(e) => handleLeadSelect(e.target.value)}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecionar lead em negociação...</option>
                      {leadsFechados.map(lead => (
                        <option key={lead._id} value={lead._id}>
                          {lead.nome} - {lead.contato}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* 🔥 CONDICIONAL: Template Selection apenas se não for edição */}
                  {!isEditMode && (
                    <div className="lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Template (Opcional)
                      </label>
                      <select
                        value={formData.templateId}
                        onChange={(e) => handleTemplateSelect(e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Criar do zero...</option>
                        {templates.filter(t => t.ativo).map(template => (
                          <option key={template._id} value={template._id}>
                            {template.nome} - {template.categoria}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Status */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value as any }))}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="rascunho">Rascunho</option>
                      <option value="enviado">Enviado</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="perdido">Perdido</option>
                      <option value="expirado">Expirado</option>
                    </select>
                  </div>

                  {/* Client Info */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Cliente *
                    </label>
                    <input
                      type="text"
                      value={formData.nomeCliente}
                      onChange={(e) => setFormData(prev => ({ ...prev, nomeCliente: e.target.value }))}
                      required
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contato do Cliente
                    </label>
                    <input
                      type="text"
                      value={formData.contatoCliente}
                      onChange={(e) => setFormData(prev => ({ ...prev, contatoCliente: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Project Info */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome do Projeto *
                    </label>
                    <input
                      type="text"
                      value={formData.nomeProjeto}
                      onChange={(e) => setFormData(prev => ({ ...prev, nomeProjeto: e.target.value }))}
                      required
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Dates */}
                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Emissão *
                    </label>
                    <input
                      type="date"
                      value={formData.dataEmissao}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataEmissao: e.target.value }))}
                      required
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="lg:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data de Validade *
                    </label>
                    <input
                      type="date"
                      value={formData.dataValidade}
                      onChange={(e) => setFormData(prev => ({ ...prev, dataValidade: e.target.value }))}
                      required
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* 🔥 NOVO: Campos BDI Específicos por Tipo de Execução e Material */}
                  <div className="lg:col-span-3">
                    <h4 className="text-md font-semibold text-gray-700 mb-4 border-b pb-2">
                      Configuração de BDI por Tipo de Execução e Material
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          BDI Administração Mão de Obra (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.bdiAdministracaoMaoObra}
                          onChange={(e) => handleSpecificBDIChange('bdiAdministracaoMaoObra', Number(e.target.value))}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          BDI Administração Material (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.bdiAdministracaoMaterial}
                          onChange={(e) => handleSpecificBDIChange('bdiAdministracaoMaterial', Number(e.target.value))}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          BDI Execução Mão de Obra (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.bdiExecucaoMaoObra}
                          onChange={(e) => handleSpecificBDIChange('bdiExecucaoMaoObra', Number(e.target.value))}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          BDI Execução Material (%)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={formData.bdiExecucaoMaterial}
                          onChange={(e) => handleSpecificBDIChange('bdiExecucaoMaterial', Number(e.target.value))}
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Descrição do Projeto
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}

            {/* Etapas */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Etapas do Projeto</h3>
                <button
                  type="button"
                  onClick={addEtapa}
                  className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus size={20} />
                  Adicionar Etapa
                </button>
              </div>

              {etapas.map((etapa, etapaIndex) => {
                if (!etapa) return null
                
                const etapaBreakdown = calculateEtapaBreakdown(etapa)
                const dragData: DragData = { type: 'etapa', etapaIndex }
                const isDropTarget = dropTarget?.type === 'etapa' && dropTarget?.etapaIndex === etapaIndex
                
                return (
                  <div key={etapaIndex} className="mb-6">
                    {/* 🔥 NOVO: Drop zone antes da etapa para promoção de subetapa */}
                    <div
                      className={`h-3 transition-all duration-200 ${
                        isDropTarget && dropTarget?.position === 'before' 
                          ? 'bg-blue-400 border-2 border-dashed border-blue-500' 
                          : draggedItem?.type === 'subetapa' 
                            ? 'bg-orange-200 border border-dashed border-orange-400' 
                            : 'bg-transparent'
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, { type: 'etapa', etapaIndex, position: 'before' })}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, { type: 'etapa', etapaIndex, position: 'before' })}
                    />
                    
                    <div 
                      className={`bg-white p-6 rounded-lg shadow-sm transition-all duration-200 ${
                        isDropTarget && dropTarget?.position === 'inside' 
                          ? 'ring-2 ring-blue-400 bg-blue-50' 
                          : ''
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, { type: 'etapa', etapaIndex, position: 'inside' })}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, { type: 'etapa', etapaIndex, position: 'inside' })}
                    >
                      <div className="flex items-center gap-4 mb-6">
                        <div
                          draggable
                          onDragStart={(e) => handleDragStart(e, dragData)}
                          onDragEnd={handleDragEnd}
                          className="cursor-move p-1 hover:bg-gray-200 rounded transition-colors"
                        >
                          <GripVertical size={20} className="text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={etapa.codigo || ''}
                          onChange={(e) => handleEtapaCodeChange(etapaIndex, e.target.value)}
                          placeholder="Código"
                          className="w-24 p-3 border border-gray-300 rounded-md"
                        />
                        <input
                          type="text"
                          value={etapa.nome || ''}
                          onChange={(e) => {
                            const newEtapas = [...etapas]
                            newEtapas[etapaIndex].nome = e.target.value
                            setEtapas(newEtapas)
                          }}
                          placeholder="Nome da etapa"
                          className="flex-1 p-3 border border-gray-300 rounded-md"
                        />

                        <button
                          type="button"
                          onClick={() => addSubetapa(etapaIndex)}
                          className="bg-green-600 text-white px-4 py-3 rounded-md hover:bg-green-700"
                        >
                          <Plus size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setCurrentEtapaIndex(etapaIndex)
                            setCurrentSubetapaPath([])
                            setShowInsumoModal(true)
                          }}
                          className="bg-purple-600 text-white px-4 py-3 rounded-md hover:bg-purple-700 flex items-center gap-2"
                        >
                          <ShoppingCart size={20} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const newEtapas = etapas.filter((_, i) => i !== etapaIndex)
                            const reorganizedEtapas = reorganizeEtapas(newEtapas)
                            setEtapas(reorganizedEtapas)
                          }}
                          className="bg-red-600 text-white px-4 py-3 rounded-md hover:bg-red-700"
                        >
                          <Trash2 size={20} />
                        </button>
                      </div>

                      {/* Totais da etapa */}
                      <div className="mb-6 text-sm">
                        <div className="flex flex-wrap gap-6">
                          <div>
                            <span className="font-medium">Material: </span>
                            <span className="text-blue-600">c/ BDI: R$ {etapaBreakdown.materialComBDI.toFixed(2)}</span>
                            <span className="ml-2">s/ BDI: R$ {etapaBreakdown.material.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="font-medium">Equipamento: </span>
                            <span className="text-blue-600">c/ BDI: R$ {etapaBreakdown.equipamentoComBDI.toFixed(2)}</span>
                            <span className="ml-2">s/ BDI: R$ {etapaBreakdown.equipamento.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="font-medium">Mão de Obra: </span>
                            <span className="text-blue-600">c/ BDI: R$ {etapaBreakdown.maoObraComBDI.toFixed(2)}</span>
                            <span className="ml-2">s/ BDI: R$ {etapaBreakdown.maoObra.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="font-medium">Composições: </span>
                            <span className="text-blue-600">c/ BDI: R$ {etapaBreakdown.composicaoComBDI.toFixed(2)}</span>
                            <span className="ml-2">s/ BDI: R$ {etapaBreakdown.composicao.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="font-bold">Total: </span>
                            <span className="text-blue-600 font-bold">c/ BDI: R$ {etapaBreakdown.totalComBDI.toFixed(2)}</span>
                            <span className="ml-2 font-bold">s/ BDI: R$ {etapaBreakdown.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      {/* Items da etapa */}
                      {etapa.itens && etapa.itens.length > 0 && (
                        <div className="mb-6">
                          <h5 className="text-sm font-medium text-gray-700 mb-3">Itens da Etapa</h5>
                          {renderItemsTable(etapa.itens, etapaIndex)}
                        </div>
                      )}

                      {/* Subetapas recursivas */}
                      {etapa.subetapas && etapa.subetapas.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-3">Subetapas</h5>
                          {renderSubetapas(etapa.subetapas, etapaIndex)}
                        </div>
                      )}
                    </div>
                    
                    {/* 🔥 NOVO: Drop zone depois da etapa para promoção de subetapa */}
                    <div
                      className={`h-3 transition-all duration-200 ${
                        isDropTarget && dropTarget?.position === 'after' 
                          ? 'bg-blue-400 border-2 border-dashed border-blue-500' 
                          : draggedItem?.type === 'subetapa' 
                            ? 'bg-orange-200 border border-dashed border-orange-400' 
                            : 'bg-transparent'
                      }`}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnter(e, { type: 'etapa', etapaIndex, position: 'after' })}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, { type: 'etapa', etapaIndex, position: 'after' })}
                    />
                  </div>
                )
              })}
            </div>

            {/* Totals */}
            <div className="bg-blue-50 p-6 rounded-lg">
              <div className="flex items-center gap-2 mb-6">
                <Calculator size={24} className="text-blue-600" />
                <h3 className="text-lg font-semibold text-blue-900">Resumo Financeiro</h3>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Subtotal (sem BDI)</p>
                  <p className="text-2xl font-bold text-gray-900">R$ {subtotal.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total (com BDI)</p>
                  <p className="text-3xl font-bold text-blue-600">R$ {total.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData(prev => ({ ...prev, observacoes: e.target.value }))}
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </form>
        </div>

        {/* Footer fixo com botões de ação */}
        <div className="sticky bottom-0 bg-white border-t p-6 shadow-lg">
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-8 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 text-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-lg"
            >
              {initialData ? 'Atualizar' : 'Criar'} Orçamento
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Seleção de Insumos/Composições */}
      {showInsumoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
          <div className="bg-white w-[80%] h-[90%] flex flex-col rounded-lg shadow-xl">
            <div className="sticky top-0 bg-white border-b p-6 z-[110] shadow-sm rounded-t-lg">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold">
                  Selecionar Itens {currentSubetapaPath.length === 0 ? '(Etapa)' : '(Subetapa)'}
                </h3>
                <button
                  onClick={() => {
                    setShowInsumoModal(false)
                    setSelectedItems(new Set())
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 z-[120]"
                >
                  <X size={24} />
                </button>
              </div>
              
              {/* 🔥 NOVO: Filtros e Busca com opções específicas de tipo */}
              <div className="space-y-4">
                <div className="flex gap-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={24} />
                    <input
                      type="text"
                      placeholder="Buscar itens..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-lg"
                    />
                  </div>
                  {/* 🔥 NOVO: Select com opções específicas de tipo */}
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value as any)}
                    className="px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-lg"
                  >
                    <option value="all">Todos</option>
                    <option value="material">Material</option>
                    <option value="mao_obra">Mão de Obra</option>
                    <option value="equipamento">Equipamento</option>
                    <option value="composicao">Composição</option>
                  </select>
                </div>

                {/* Controles de Seleção */}
                <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-6">
                    <span className="text-lg font-medium">
                      {selectedItems.size} item(s) selecionado(s)
                    </span>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={selectAllItems}
                        className="text-lg text-blue-600 hover:text-blue-800"
                      >
                        Selecionar Todos
                      </button>
                      <button
                        type="button"
                        onClick={clearSelection}
                        className="text-lg text-gray-600 hover:text-gray-800"
                      >
                        Limpar Seleção
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={addSelectedItemsToTarget}
                    disabled={selectedItems.size === 0}
                    className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 text-lg"
                  >
                    <Plus size={20} />
                    Adicionar Selecionados ({selectedItems.size})
                  </button>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <Search size={64} className="mx-auto mb-4 text-gray-300" />
                  <p className="text-xl text-gray-500">Nenhum item encontrado</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {filteredItems.map(item => (
                    <div
                      key={item._id}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedItems.has(item._id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => toggleItemSelection(item._id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(item._id)}
                            onChange={() => toggleItemSelection(item._id)}
                            className="w-5 h-5 text-blue-600"
                          />
                          <div>
                            <h5 className="font-medium text-lg">{item.nome}</h5>
                            <div className="flex items-center gap-3 mt-1">
                              {/* 🔥 CORRIGIDO: Mostrar tipo específico em vez de apenas "insumo" */}
                              {item.tipo === 'insumo' && item.tipo && (
                                <span className={`px-3 py-1 rounded text-sm ${
                                  item.tipo === 'material' ? 'bg-green-100 text-green-800' :
                                  item.tipo === 'equipamento' ? 'bg-orange-100 text-orange-800' :
                                  item.tipo === 'mao_obra' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {item.tipo === 'mao_obra' ? 'Mão de Obra' : 
                                   item.tipo.charAt(0).toUpperCase() + item.tipo.slice(1)}
                                </span>
                              )}
                              {item.tipo === 'composicao' && (
                                <span className="px-3 py-1 rounded bg-blue-100 text-blue-800 text-sm">
                                  Composição
                                </span>
                              )}
                              {item.tipo === 'insumo' && !item.tipo && (
                                <span className="px-3 py-1 rounded bg-green-100 text-green-800 text-sm">
                                  Material
                                </span>
                              )}
                              <span className="text-gray-600">{item.unidadeMedida}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-lg">
                            R$ {Number(item.precoUnitario || item.valorTotal || 0).toFixed(2)}
                          </p>
                          {item.tipo === 'insumo' && (
                            <p className="text-gray-600">Estoque: {item.quantidadeEstoque || 0}</p>
                          )}
                          {item.tipo === 'composicao' && (
                            <p className="text-gray-600">{item.itens?.length || 0} itens</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrcamentoForm
